import { Api } from "../../../../../utils/Api";
import { siteName } from "./siteName";
import { LinkAttachment } from "./types";

export interface FetchedLinkMetadata {
  title?: string;
  description?: string;
  image?: string;
  favicon?: string;
  hostname?: string;
  url?: string;
}

interface LinkPreviewResponse {
  metadata: FetchedLinkMetadata | null;
}

// Eagerly unfurls a URL against the server the instant it's added to the
// composer — no debounce, since adding a link is a discrete commit event
// (paste / "Add" click), never a keystroke. Resolves to null on any
// failure: network error, non-2xx, rate limit, or a clean "couldn't
// unfurl this" response — all of those fall back to a bare chip the same
// way. Resolving null on an aborted request is fine too: the caller
// guards every state update on the attachment still being present, so an
// abort (link removed) just becomes a harmless no-op.
export const fetchLinkPreview = (
  url: string,
  signal: AbortSignal,
): Promise<FetchedLinkMetadata | null> => {
  const token = $('meta[name="csrf-token"]').attr("content");

  return new Api({})
    .post<LinkPreviewResponse, { url: string }>(
      "/api/community/link_previews.json",
      { url },
      { signal, headers: { "X-CSRF-Token": token } },
    )
    .then((response) => response.data.metadata)
    .catch(() => null);
};

type LinkBase = Pick<LinkAttachment, "id" | "kind" | "url" | "domain" | "videoId">;

// Builds the "resolved" or "bare" attachment for a base link once we know
// whether the unfurl produced anything — shared by the eager-fetch
// resolution/timeout path and by edit-mode hydration from a saved post's
// link rows, so both states are built exactly the same way.
export const toResolvedLinkAttachment = (
  base: LinkBase,
  metadata: FetchedLinkMetadata | null,
): LinkAttachment => {
  if (metadata && (metadata.title || metadata.image || metadata.description)) {
    return {
      ...base,
      domain: metadata.hostname || base.domain,
      status: "resolved",
      title: metadata.title,
      description: metadata.description,
      image: metadata.image,
      favicon: metadata.favicon,
    };
  }

  return {
    ...base,
    status: "bare",
    site: siteName(base.domain),
  };
};
